import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Card,
  Box,
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tabs,
  Tab
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WrapperSeamless from '../../layout-components/WrapperSeamless';
import Upload from '../../components/upload';
import CustomSelect from '../../components/select';
import * as Api from '../../service';
import useStore from '../../context';
import ReactQuill from 'react-quill';
import { contentHasRiskyMarkup } from '../../utils/rich-text';

const initForm = {
  title: '',
  slug: '',
  summary: '',
  content: '',
  metaTitle: '',
  metaDescription: '',
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  featuredImageUrl: '',
  featuredImageAlt: '',
  focusKeyword: '',
  schemaJson: '',
  authorNameOverride: '',
  authorBioOverride: '',
  status: 'DRAFT',
  publishAt: '',
  lastUpdatedAt: '',
  tagIds: [],
  categoryIds: [],
  // no longer editable here, but must round-trip on save: the API clears
  // stored rows when the payload has an empty images array
  images: [],
  relatedPostIds: []
};

const MAX_META_TITLE = 60;
const MAX_META_DESC = 160;
const MAX_AUTHOR_BIO = 300;

const slugify = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const slugIsValid = (value) =>
  !value || /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);

const toDisplayDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toISOString().slice(0, 16);
};

const toIso = (localValue) => {
  if (!localValue) return '';
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return localValue;
  return date.toISOString();
};

const stripHtml = (html) => html.replace(/<[^>]*>/g, ' ');

const isImageFile = (file) => !!file && !!file.type && file.type.startsWith('image/');

const firstImageFile = (files) => Array.from(files || []).find(isImageFile);

// Quill 1 exposes no point-to-index lookup, so resolve a drop point through the
// DOM caret and translate the node it lands in back into an editor index. Falls
// back to the end of the document when the browser cannot place a caret.
const indexFromPoint = (editor, x, y) => {
  let node = null;
  let offset = 0;

  if (document.caretRangeFromPoint) {
    const range = document.caretRangeFromPoint(x, y);
    if (range) {
      node = range.startContainer;
      offset = range.startOffset;
    }
  } else if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(x, y);
    if (position) {
      node = position.offsetNode;
      offset = position.offset;
    }
  }

  if (!node) return editor.getLength();
  const blot = ReactQuill.Quill.find(node, true);
  if (!blot) return editor.getLength();
  return editor.getIndex(blot) + offset;
};

const extractHeadings = (html, tag) => {
  const regex = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi');
  const headings = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    const text = stripHtml(match[1]).replace(/\s+/g, ' ').trim();
    if (text) headings.push(text);
  }
  return headings;
};

const toIdArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => (entry && entry.id ? entry.id : entry));
};

const normalizeForm = (data) => {
  if (!data) return initForm;
  return {
    ...initForm,
    ...data,
    title: data.title || '',
    slug: data.slug || '',
    summary: data.summary || '',
    content: data.content || '',
    metaTitle: data.metaTitle || '',
    metaDescription: data.metaDescription || '',
    canonicalUrl: data.canonicalUrl || '',
    ogTitle: data.ogTitle || '',
    ogDescription: data.ogDescription || '',
    ogImage: data.ogImage || '',
    featuredImageUrl: data.featuredImageUrl || '',
    featuredImageAlt: data.featuredImageAlt || '',
    focusKeyword: data.focusKeyword || '',
    schemaJson: data.schemaJson || '',
    authorNameOverride: data.authorNameOverride || '',
    authorBioOverride: data.authorBioOverride || '',
    tagIds: toIdArray(data.tagIds?.length ? data.tagIds : data.tags),
    categoryIds: toIdArray(
      data.categoryIds?.length ? data.categoryIds : data.categories
    ),
    images: Array.isArray(data.images) ? data.images : [],
    relatedPostIds: Array.isArray(data.relatedPostIds) ? data.relatedPostIds : [],
    lastUpdatedAt: data.lastUpdatedAt || data.updatedAt || ''
  };
};

const BlogForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const { setErrorMsg, setSuccessMsg } = useStore();

  const [form, setForm] = useState(initForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [autoSchema, setAutoSchema] = useState(true);
  const [relatedText, setRelatedText] = useState('');
  const [featuredFile, setFeaturedFile] = useState(null);
  const [ogFile, setOgFile] = useState(null);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [relatedOptions, setRelatedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [newCategory, setNewCategory] = useState({ name: '', slug: '' });

  // null until loadPost has seen the content and picked a tab. Mounting the
  // rich editor even for the render it takes to switch tabs hands the HTML to
  // Quill, which reports its own reformatting back through onChange as if the
  // author had made it — so the guard below has to decide before that happens.
  const [contentTab, setContentTab] = useState(id ? null : 'rich');
  const [contentWarning, setContentWarning] = useState(false);
  // { loading } while uploading, then { url, alt, index, previewLoaded };
  // null when the dialog is closed
  const [altDialog, setAltDialog] = useState(null);
  // editor index of the currently selected inline image, otherwise null
  const [selectedImage, setSelectedImage] = useState(null);
  const quillRef = useRef(null);
  const contentGuardRanRef = useRef(false);
  // Content exactly as it was loaded. Simply opening the rich-text tab hands
  // the HTML to Quill, which drops anything it cannot model (a table becomes
  // one run-on paragraph) and reports that back as an edit. Keeping the
  // original lets the author undo that instead of discovering it after saving.
  const originalContentRef = useRef(null);

  const handleQuillChange = useCallback((value) => {
    // Quill reports an empty document as '<p><br></p>'
    setForm((st) => ({ ...st, content: value === '<p><br></p>' ? '' : value }));
  }, []);

  const loadTags = useCallback(() => {
    Api.tags
      .getAll()
      .then((res) => setTags(Array.isArray(res) ? res : res?.items || []))
      .catch(() => {});
  }, []);

  const loadCategories = useCallback(() => {
    Api.blog
      .getCategories()
      .then((res) => setCategories(Array.isArray(res) ? res : res?.items || []))
      .catch(() => {});
  }, []);

  const loadRelatedOptions = useCallback(() => {
    Api.blog
      .getAll({ limit: 50, offset: 0, sort: 'publishAt:DESC' })
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        const options = items.map((post) => ({
          id: post.id || post._id,
          name: post.title || post.metaTitle || post.slug || 'Untitled'
        }));
        setRelatedOptions(options);
      })
      .catch(() => {});
  }, []);

  const loadPost = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await Api.blog.getById(id);
      const normalized = normalizeForm(res);
      originalContentRef.current = normalized.content;
      // Pick the tab before the content reaches state, so content Quill would
      // mangle never reaches Quill in the first place.
      if (!contentGuardRanRef.current) {
        contentGuardRanRef.current = true;
        const risky = contentHasRiskyMarkup(normalized.content);
        setContentTab(risky ? 'html' : 'rich');
        setContentWarning(risky);
      }
      setForm(normalized);
      setRelatedText((normalized.relatedPostIds || []).join(','));
      setAutoSchema(!normalized.schemaJson);
    } catch (e) {
      setContentTab((tab) => tab || 'rich');
      setErrorMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTags();
    loadCategories();
    loadRelatedOptions();
    loadPost();
  }, []);

  // The sticky editor toolbar is disabled by any ancestor with a non-visible
  // overflow, and both the layout panel and every MUI Card set overflow:hidden.
  // Flag the page so the stylesheet can relax those, and only those.
  useEffect(() => {
    document.body.classList.add('has-sticky-editor');
    return () => document.body.classList.remove('has-sticky-editor');
  }, []);

  // Derive the slug from the title only while drafting a new post. An existing
  // post keeps the slug it was saved with: the slug is its public URL, and the
  // API rewrites the URL and registers a 301 redirect whenever it changes. This
  // effect used to run after loadPost filled in the title, which silently
  // replaced a hand-tuned slug with slugify(title) before the author saw it.
  useEffect(() => {
    if (id || slugTouched) return;
    const nextSlug = slugify(form.title || '');
    if (form.slug !== nextSlug) {
      setForm((st) => ({ ...st, slug: nextSlug }));
    }
  }, [form.title, slugTouched, id]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((st) => ({ ...st, [name]: value }));
  };

  const uploadImage = async (file, context) => {
    const data = new FormData();
    data.append('context', context);
    data.append('file', file);
    return Api.blog.uploadImage(data);
  };

  // Upload, then hand off to the alt-text dialog, which inserts at `index` so
  // the image lands where the author asked for it rather than at the caret at
  // the time the dialog closes.
  const startImageUpload = async (file, index) => {
    setAltDialog({ loading: true });
    try {
      const res = await uploadImage(file, 'BLOG_IMAGE');
      const url = res.url || '';
      if (!url) throw new Error('Image upload returned no URL');
      const defaultAlt = file.name.replace(/\.[^/.]+$/, '');
      setAltDialog({ url, alt: defaultAlt, index, previewLoaded: false });
    } catch (e) {
      setAltDialog(null);
      setErrorMsg(e.message);
    }
  };

  const handleQuillImage = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) return;
      const editor = quillRef.current && quillRef.current.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true);
      startImageUpload(file, range ? range.index : editor.getLength());
    };
    input.click();
  };

  const insertInlineImage = () => {
    if (!altDialog || altDialog.loading || !altDialog.url) return;
    const { url, alt, index } = altDialog;
    setAltDialog(null);
    const editor = quillRef.current && quillRef.current.getEditor();
    if (!editor) return;
    const before = new Set(editor.root.querySelectorAll('img'));
    editor.insertEmbed(index, 'image', url, 'user');
    const inserted = Array.from(editor.root.querySelectorAll('img')).find(
      (node) => !before.has(node)
    );
    if (inserted && alt) inserted.setAttribute('alt', alt);
    setForm((st) => ({ ...st, content: editor.root.innerHTML }));
    editor.setSelection(index + 1, 0);
  };

  // Clicking an image in the editor selects it as an embed (length-1 range),
  // which enables Delete/Backspace and drives the "Remove image" button below
  // the editor. Quill clears the selection on blur, which resets the state.
  useEffect(() => {
    if (contentTab !== 'rich') return undefined;
    const editor = quillRef.current && quillRef.current.getEditor();
    if (!editor) return undefined;

    const handleClick = (e) => {
      if (!e.target || e.target.tagName !== 'IMG') return;
      const blot = ReactQuill.Quill.find(e.target);
      if (!blot) return;
      editor.setSelection(editor.getIndex(blot), 1, 'user');
    };

    const handleSelectionChange = (range) => {
      if (!range || range.length !== 1) {
        setSelectedImage(null);
        return;
      }
      const op = editor.getContents(range.index, 1).ops?.[0];
      setSelectedImage(op?.insert?.image ? range.index : null);
    };

    editor.root.addEventListener('click', handleClick);
    editor.on('selection-change', handleSelectionChange);
    return () => {
      editor.root.removeEventListener('click', handleClick);
      editor.off('selection-change', handleSelectionChange);
    };
  }, [contentTab]);

  // Dropping or pasting an image uploads it and inserts it at that spot, so a
  // long post no longer needs a trip up to the toolbar for every image. Left to
  // the browser, a dropped file is inlined as a base64 data URL, which
  // submitForm rejects — so both handlers preventDefault.
  const startImageUploadRef = useRef(startImageUpload);
  startImageUploadRef.current = startImageUpload;

  useEffect(() => {
    if (contentTab !== 'rich') return undefined;
    const editor = quillRef.current && quillRef.current.getEditor();
    if (!editor) return undefined;

    const handleDrop = (e) => {
      const file = firstImageFile(e.dataTransfer && e.dataTransfer.files);
      if (!file) return;
      e.preventDefault();
      startImageUploadRef.current(
        file,
        indexFromPoint(editor, e.clientX, e.clientY)
      );
    };

    const handlePaste = (e) => {
      const file = firstImageFile(e.clipboardData && e.clipboardData.files);
      if (!file) return;
      e.preventDefault();
      // getSelection() reports null mid-paste even with the caret in the
      // editor, which would drop every pasted image at the end of the post.
      // The truthy argument restores the saved range first, as the toolbar
      // button does.
      const range = editor.getSelection(true);
      startImageUploadRef.current(file, range ? range.index : editor.getLength());
    };

    editor.root.addEventListener('drop', handleDrop);
    editor.root.addEventListener('paste', handlePaste);
    return () => {
      editor.root.removeEventListener('drop', handleDrop);
      editor.root.removeEventListener('paste', handlePaste);
    };
  }, [contentTab]);

  const removeSelectedImage = () => {
    const editor = quillRef.current && quillRef.current.getEditor();
    if (!editor || selectedImage == null) return;
    editor.deleteText(selectedImage, 1, 'user');
    setSelectedImage(null);
  };

  // Keep quillModules referentially stable while always calling the latest
  // handler (which closes over current state).
  const quillImageHandlerRef = useRef(handleQuillImage);
  quillImageHandlerRef.current = handleQuillImage;

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['blockquote', 'link', 'image', 'video'],
          ['clean']
        ],
        handlers: {
          image: () => quillImageHandlerRef.current()
        }
      }
    }),
    []
  );

  const uploadFeatured = async () => {
    if (!featuredFile?.length) return;
    try {
      const res = await uploadImage(featuredFile[0], 'BLOG_FEATURED');
      setForm((st) => ({ ...st, featuredImageUrl: res.url || '' }));
      setFeaturedFile(null);
      setSuccessMsg('featured image uploaded');
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const uploadOg = async () => {
    if (!ogFile?.length) return;
    try {
      const res = await uploadImage(ogFile[0], 'BLOG_OG');
      setForm((st) => ({ ...st, ogImage: res.url || '' }));
      setOgFile(null);
      setSuccessMsg('og image uploaded');
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const canonicalPreview =
    form.canonicalUrl ||
    (form.slug ? `https://doodooc.com/blog/${form.slug}` : '');

  const focusKeyword = (form.focusKeyword || '').trim();
  const contentText = useMemo(
    () => stripHtml(form.content || '').replace(/\s+/g, ' ').trim(),
    [form.content]
  );
  const keywordRegex = focusKeyword
    ? new RegExp(`\\b${focusKeyword.replace(/[-/\\^$*+?.()|[\\]{}]/g, '\\$&')}\\b`, 'gi')
    : null;
  const keywordCount = keywordRegex
    ? (contentText.match(keywordRegex) || []).length
    : 0;
  const h1Headings = useMemo(() => extractHeadings(form.content || '', 'h1'), [
    form.content
  ]);
  const h2Headings = useMemo(() => extractHeadings(form.content || '', 'h2'), [
    form.content
  ]);

  const seoWarnings = [];
  if (h1Headings.length === 0) seoWarnings.push('Add exactly one H1.');
  if (h1Headings.length > 1) seoWarnings.push('Only one H1 is recommended.');
  if (!focusKeyword) seoWarnings.push('Focus keyword is empty.');
  if (focusKeyword && keywordCount === 0)
    seoWarnings.push('Focus keyword not found in content.');

  const schemaObject = useMemo(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: form.title || form.metaTitle || '',
      description: form.metaDescription || form.summary || '',
      image: form.ogImage || form.featuredImageUrl || undefined,
      datePublished: form.publishAt || undefined,
      dateModified: form.lastUpdatedAt || undefined,
      author: form.authorNameOverride
        ? {
            '@type': 'Person',
            name: form.authorNameOverride,
            description: form.authorBioOverride || undefined
          }
        : undefined,
      mainEntityOfPage: canonicalPreview || undefined
    };

    Object.keys(schema).forEach((key) => {
      if (schema[key] === undefined || schema[key] === '') {
        delete schema[key];
      }
    });

    return schema;
  }, [form, canonicalPreview]);

  const schemaJson = autoSchema
    ? JSON.stringify(schemaObject, null, 2)
    : form.schemaJson;

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (!slugIsValid(form.slug)) {
          return setErrorMsg('slug must be lowercase with dashes only');
        }
        if (/src\s*=\s*["']data:/i.test(form.content || '')) {
          setErrorMsg(
            'Content contains embedded (base64) images. Remove them and use the rich editor image button to upload instead.'
          );
          return;
        }
        const relatedPostIds = relatedText
          ? relatedText
              .split(',')
              .map((val) => val.trim())
              .filter(Boolean)
              .map((val) => Number(val) || val)
          : form.relatedPostIds;
        const payload = {
          ...form,
          publishAt: form.publishAt || undefined,
          lastUpdatedAt: form.lastUpdatedAt || undefined,
          relatedPostIds,
          schemaJson: schemaJson || undefined
        };
        const data = JSON.stringify(payload);
        const res = id
          ? await Api.blog.update(id, data)
          : await Api.blog.create(data);
        if (!id && res?.id) {
          history.push(`/blog/create/${res.id}`);
        }
        setSuccessMsg(id ? 'updated' : 'created');
      } catch (e) {
        setErrorMsg(e.message);
      }
    },
    [form, relatedText, id, schemaJson]
  );

  const publishNow = useCallback(async () => {
    if (!id) return;
    try {
      await Api.blog.publish(id);
      setSuccessMsg('published');
      loadPost();
    } catch (e) {
      setErrorMsg(e.message);
    }
  }, [id]);

  const schedulePost = useCallback(async () => {
    if (!id) return;
    if (!form.publishAt) return setErrorMsg('publishAt required');
    try {
      await Api.blog.schedule(id, form.publishAt);
      setSuccessMsg('scheduled');
      loadPost();
    } catch (e) {
      setErrorMsg(e.message);
    }
  }, [id, form.publishAt]);

  const addTag = async () => {
    if (!newTag.trim()) return;
    try {
      await Api.tags.addNew(JSON.stringify({ name: newTag.trim() }));
      setNewTag('');
      setSuccessMsg('tag added');
      loadTags();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const addCategory = async () => {
    if (!newCategory.name.trim() || !newCategory.slug.trim()) {
      return setErrorMsg('category name and slug required');
    }
    try {
      await Api.blog.createCategory(JSON.stringify(newCategory));
      setNewCategory({ name: '', slug: '' });
      setSuccessMsg('category added');
      loadCategories();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const updateRelatedFromSelect = (ids) => {
    setForm((st) => ({ ...st, relatedPostIds: ids }));
    setRelatedText(ids.join(','));
  };

  const breadcrumbPreview = useMemo(() => {
    const categoryNames = categories
      .filter((cat) => form.categoryIds.includes(cat.id))
      .map((cat) => cat.name);
    const parts = ['Home', 'Blog', ...categoryNames];
    return parts.join(' > ');
  }, [categories, form.categoryIds]);

  const shareLinks = useMemo(() => {
    if (!canonicalPreview) return null;
    const encodedUrl = encodeURIComponent(canonicalPreview);
    const encodedTitle = encodeURIComponent(form.title || form.metaTitle || '');
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
    };
  }, [canonicalPreview, form.title, form.metaTitle]);

  return (
    <Container>
      <WrapperSeamless
        sectionHeading={id ? 'Edit Blog Post' : 'Create Blog Post'}
        backUrl="/blog"
      >
        <Card className="shadow-xxl px-4 py-2">
          <Box component="form" onSubmit={submitForm}>
            <Card className="shadow-xxl px-4 py-3 mb-3">
              <div className="d-flex flex-wrap align-items-center">
                <FormControl
                  variant="outlined"
                  className="my-2 mr-3"
                  style={{ minWidth: 200 }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    name="status"
                    value={form.status}
                    onChange={updateField}
                  >
                    <MenuItem value="DRAFT">DRAFT</MenuItem>
                    <MenuItem value="PUBLISHED">PUBLISHED</MenuItem>
                    <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  className="my-2 mr-3"
                  label="Publish At"
                  type="datetime-local"
                  variant="outlined"
                  value={toDisplayDate(form.publishAt)}
                  onChange={(e) =>
                    setForm((st) => ({
                      ...st,
                      publishAt: toIso(e.target.value)
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  className="my-2 mr-3"
                  label="Last Updated"
                  type="datetime-local"
                  variant="outlined"
                  value={toDisplayDate(form.lastUpdatedAt)}
                  onChange={(e) =>
                    setForm((st) => ({
                      ...st,
                      lastUpdatedAt: toIso(e.target.value)
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </Card>

            <TextField
              className="my-2"
              label="Title"
              variant="outlined"
              name="title"
              fullWidth
              value={form.title}
              onChange={(e) => {
                updateField(e);
              }}
            />
            <TextField
              className="my-2"
              label="Slug"
              variant="outlined"
              name="slug"
              fullWidth
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                updateField(e);
              }}
              error={!slugIsValid(form.slug)}
              helperText={
                slugIsValid(form.slug)
                  ? 'Lowercase, dashes only'
                  : 'Invalid slug format'
              }
            />
            <TextField
              className="my-2"
              label="Summary"
              variant="outlined"
              name="summary"
              fullWidth
              multiline
              value={form.summary}
              onChange={updateField}
            />
            <Card className="shadow-xxl px-4 py-3 my-4">
              <Tabs
                value={contentTab || 'rich'}
                onChange={(e, value) => setContentTab(value)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Rich text" value="rich" disabled={!contentTab} />
                <Tab label="HTML" value="html" disabled={!contentTab} />
              </Tabs>
              {contentWarning && (
                <Card className="p-3 my-2" style={{ background: '#fff8e1' }}>
                  <div className="d-flex align-items-center">
                    <span className="flex-grow-1 font-size-sm">
                      This post contains a table or image caption that the rich
                      editor cannot represent and would flatten into plain
                      paragraphs. Edit it as HTML to keep it intact.
                    </span>
                    {contentTab !== 'html' && (
                      <Button
                        size="small"
                        className="btn-neutral-warning mr-2"
                        onClick={() => setContentTab('html')}
                      >
                        Edit as HTML
                      </Button>
                    )}
                    {originalContentRef.current != null &&
                      form.content !== originalContentRef.current && (
                        <Button
                          size="small"
                          className="btn-neutral-danger mr-2"
                          onClick={() => {
                            setForm((st) => ({
                              ...st,
                              content: originalContentRef.current
                            }));
                            setContentTab('html');
                          }}
                        >
                          Restore original
                        </Button>
                      )}
                    <Button size="small" onClick={() => setContentWarning(false)}>
                      Dismiss
                    </Button>
                  </div>
                </Card>
              )}
              {!contentTab ? (
                <div className="text-center text-black-50 font-size-sm py-5">
                  <CircularProgress size={24} />
                  <div className="mt-2">Loading content...</div>
                </div>
              ) : contentTab === 'rich' ? (
                <>
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={form.content}
                    onChange={handleQuillChange}
                    modules={quillModules}
                    className="my-2"
                  />
                  {selectedImage != null && (
                    <div className="d-flex align-items-center my-2">
                      <span className="font-size-sm text-black-50 mr-3">
                        Image selected — press Delete or
                      </span>
                      <Button
                        size="small"
                        className="btn-neutral-danger"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={removeSelectedImage}
                      >
                        Remove image
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <TextField
                  className="my-2"
                  label="Content (HTML)"
                  variant="outlined"
                  name="content"
                  fullWidth
                  multiline
                  rows={12}
                  value={form.content}
                  onChange={updateField}
                />
              )}
            </Card>

            <Dialog
              open={!!altDialog}
              onClose={() => setAltDialog(null)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle>Insert image</DialogTitle>
              <DialogContent>
                {(altDialog?.loading || (altDialog?.url && !altDialog.previewLoaded)) && (
                  <div className="text-center py-4">
                    <CircularProgress size={32} />
                    <div className="font-size-sm text-black-50 mt-2">
                      {altDialog?.loading ? 'Uploading image...' : 'Loading preview...'}
                    </div>
                  </div>
                )}
                {altDialog?.url && (
                  <img
                    src={altDialog.url}
                    alt="preview"
                    onLoad={() =>
                      setAltDialog((st) => st && { ...st, previewLoaded: true })
                    }
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      marginBottom: 12,
                      display: altDialog.previewLoaded ? 'block' : 'none'
                    }}
                  />
                )}
                {!altDialog?.loading && (
                  <TextField
                    autoFocus
                    fullWidth
                    variant="outlined"
                    label="Alt text"
                    value={altDialog?.alt || ''}
                    onChange={(e) => {
                      const alt = e.target.value;
                      setAltDialog((st) => ({ ...st, alt }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        insertInlineImage();
                      }
                    }}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAltDialog(null)}>Cancel</Button>
                <Button
                  className="btn-primary"
                  disabled={!!altDialog?.loading || !altDialog?.url}
                  onClick={insertInlineImage}
                >
                  Insert
                </Button>
              </DialogActions>
            </Dialog>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">SEO</div>
              <TextField
                className="my-2"
                label={`Meta Title (${form.metaTitle.length}/${MAX_META_TITLE})`}
                variant="outlined"
                name="metaTitle"
                fullWidth
                inputProps={{ maxLength: MAX_META_TITLE }}
                value={form.metaTitle}
                onChange={updateField}
              />
              <TextField
                className="my-2"
                label={`Meta Description (${form.metaDescription.length}/${MAX_META_DESC})`}
                variant="outlined"
                name="metaDescription"
                fullWidth
                multiline
                inputProps={{ maxLength: MAX_META_DESC }}
                value={form.metaDescription}
                onChange={updateField}
              />
              <Card className="p-3 my-2">
                <div className="text-black-50 font-size-sm">Google Preview</div>
                <div className="font-size-sm text-primary">
                  {canonicalPreview || 'https://doodooc.com/blog/your-slug'}
                </div>
                <div className="font-size-lg font-weight-bold">
                  {form.metaTitle || form.title || 'Meta Title'}
                </div>
                <div className="font-size-sm text-black-50">
                  {form.metaDescription || 'Meta description preview...'}
                </div>
              </Card>
              <TextField
                className="my-2"
                label="Canonical URL"
                variant="outlined"
                name="canonicalUrl"
                fullWidth
                value={form.canonicalUrl}
                onChange={updateField}
                helperText={
                  form.canonicalUrl
                    ? 'Custom canonical URL'
                    : 'If blank, uses the post URL'
                }
              />
              <TextField
                className="my-2"
                label="Focus Keyword"
                variant="outlined"
                name="focusKeyword"
                fullWidth
                value={form.focusKeyword}
                onChange={updateField}
              />
              <Card className="p-3 my-2">
                <div className="heading-3">SEO Checks</div>
                <div className="font-size-sm text-black-50">
                  H1: {h1Headings.length} | H2: {h2Headings.length} | Focus keyword in content: {keywordCount}
                </div>
                {seoWarnings.map((warn) => (
                  <div key={warn} className="font-size-sm text-warning">
                    {warn}
                  </div>
                ))}
              </Card>
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Open Graph</div>
              <TextField
                className="my-2"
                label="OG Title"
                variant="outlined"
                name="ogTitle"
                fullWidth
                value={form.ogTitle}
                onChange={updateField}
              />
              <TextField
                className="my-2"
                label="OG Description"
                variant="outlined"
                name="ogDescription"
                fullWidth
                multiline
                value={form.ogDescription}
                onChange={updateField}
              />
              <TextField
                className="my-2"
                label="OG Image URL"
                variant="outlined"
                name="ogImage"
                fullWidth
                value={form.ogImage}
                onChange={updateField}
              />
              {form.ogImage && (
                <img
                  src={form.ogImage}
                  alt="og"
                  style={{ maxWidth: 240, marginTop: 12 }}
                />
              )}
              <Card className="p-3 my-3">
                <div className="heading-3 mb-2">Upload OG Image</div>
                <Upload single setNewFile={setOgFile} />
                <div className="d-flex align-items-center">
                  <Button
                    type="button"
                    onClick={uploadOg}
                    className="btn-primary mt-2"
                    disabled={!ogFile?.length}
                  >
                    Upload OG
                  </Button>
                  <Button
                    type="button"
                    className="btn-neutral-first mt-2 ml-2"
                    onClick={() =>
                      setForm((st) => ({
                        ...st,
                        ogImage: st.featuredImageUrl || ''
                      }))
                    }
                    disabled={!form.featuredImageUrl}
                  >
                    Use Featured
                  </Button>
                </div>
              </Card>
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Featured Image</div>
              <TextField
                className="my-2"
                label="Featured Image URL"
                variant="outlined"
                name="featuredImageUrl"
                fullWidth
                value={form.featuredImageUrl}
                onChange={updateField}
              />
              <TextField
                className="my-2"
                label="Featured Image Alt"
                variant="outlined"
                name="featuredImageAlt"
                fullWidth
                value={form.featuredImageAlt}
                onChange={updateField}
              />
              {form.featuredImageUrl && (
                <img
                  src={form.featuredImageUrl}
                  alt={form.featuredImageAlt || 'featured'}
                  style={{ maxWidth: 240, marginTop: 12 }}
                />
              )}
              <Card className="p-3 my-3">
                <div className="heading-3 mb-2">Upload Featured Image</div>
                <Upload single setNewFile={setFeaturedFile} />
                <Button
                  type="button"
                  onClick={uploadFeatured}
                  className="btn-primary mt-2"
                  disabled={!featuredFile?.length}
                >
                  Upload Featured
                </Button>
              </Card>
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Structured Data</div>
              <div className="d-flex align-items-center mb-2">
                <Switch
                  checked={autoSchema}
                  onChange={(e) => setAutoSchema(e.target.checked)}
                  color="primary"
                />
                <span className="ml-2">Auto-generate schema</span>
              </div>
              <TextField
                className="my-2"
                label="Schema JSON"
                variant="outlined"
                name="schemaJson"
                fullWidth
                multiline
                rows={6}
                value={schemaJson}
                onChange={(e) =>
                  setForm((st) => ({ ...st, schemaJson: e.target.value }))
                }
                disabled={autoSchema}
              />
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Author Overrides</div>
              <TextField
                className="my-2"
                label="Author Name"
                variant="outlined"
                name="authorNameOverride"
                fullWidth
                value={form.authorNameOverride}
                onChange={updateField}
              />
              <TextField
                className="my-2"
                label={`Author Bio (${form.authorBioOverride.length}/${MAX_AUTHOR_BIO})`}
                variant="outlined"
                name="authorBioOverride"
                fullWidth
                multiline
                inputProps={{ maxLength: MAX_AUTHOR_BIO }}
                value={form.authorBioOverride}
                onChange={updateField}
              />
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Tags & Categories</div>
              <div className="d-flex flex-wrap">
                <div style={{ minWidth: 260 }} className="mr-3">
                  <CustomSelect
                    noCard
                    title="Tags"
                    multiple
                    data={tags || []}
                    value={form.tagIds || []}
                    onChange={(val) => setForm((st) => ({ ...st, tagIds: val }))}
                  />
                </div>
                <div style={{ minWidth: 260 }}>
                  <CustomSelect
                    noCard
                    title="Categories"
                    multiple
                    data={categories || []}
                    value={form.categoryIds || []}
                    onChange={(val) =>
                      setForm((st) => ({ ...st, categoryIds: val }))
                    }
                  />
                </div>
              </div>
              <Card className="p-3 my-2">
                <div className="heading-3 mb-2">Quick Add Tag</div>
                <div className="d-flex">
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="Tag Name"
                    fullWidth
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                  />
                  <Button className="btn-neutral-success" onClick={addTag}>
                    Add
                  </Button>
                </div>
              </Card>
              <Card className="p-3 my-2">
                <div className="heading-3 mb-2">Quick Add Category</div>
                <div className="d-flex flex-wrap">
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="Name"
                    fullWidth
                    value={newCategory.name}
                    onChange={(e) =>
                      setNewCategory((st) => ({ ...st, name: e.target.value }))
                    }
                  />
                  <TextField
                    variant="outlined"
                    className="my-2 mr-2"
                    label="Slug"
                    fullWidth
                    value={newCategory.slug}
                    onChange={(e) =>
                      setNewCategory((st) => ({ ...st, slug: e.target.value }))
                    }
                  />
                  <Button className="btn-neutral-success" onClick={addCategory}>
                    Add
                  </Button>
                </div>
              </Card>
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Breadcrumbs Preview</div>
              <div className="font-size-sm text-black-50">
                {breadcrumbPreview || 'Home > Blog'}
              </div>
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Table of Contents</div>
              {h1Headings.length === 0 && h2Headings.length === 0 && (
                <div className="font-size-sm text-black-50">
                  Add H1/H2 headings in content to generate TOC.
                </div>
              )}
              {h1Headings.map((heading, index) => (
                <div key={`h1-${index}`} className="font-size-sm font-weight-bold">
                  {heading}
                </div>
              ))}
              {h2Headings.map((heading, index) => (
                <div key={`h2-${index}`} className="font-size-sm text-black-50">
                  {heading}
                </div>
              ))}
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Related Articles</div>
              <CustomSelect
                noCard
                title="Related Posts"
                multiple
                data={relatedOptions}
                value={form.relatedPostIds}
                onChange={updateRelatedFromSelect}
              />
              <TextField
                className="my-2"
                label="Related Post IDs (comma separated)"
                variant="outlined"
                fullWidth
                value={relatedText}
                onChange={(e) => setRelatedText(e.target.value)}
              />
            </Card>

            <Card className="shadow-xxl px-4 py-3 my-4">
              <div className="heading-3 mb-2">Social Sharing Links</div>
              {shareLinks ? (
                <>
                  <TextField
                    className="my-2"
                    label="Facebook Share"
                    variant="outlined"
                    fullWidth
                    value={shareLinks.facebook}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    className="my-2"
                    label="X / Twitter Share"
                    variant="outlined"
                    fullWidth
                    value={shareLinks.twitter}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    className="my-2"
                    label="LinkedIn Share"
                    variant="outlined"
                    fullWidth
                    value={shareLinks.linkedin}
                    InputProps={{ readOnly: true }}
                  />
                </>
              ) : (
                <div className="font-size-sm text-black-50">
                  Add a slug or canonical URL to generate share links.
                </div>
              )}
            </Card>

            <div className="text-center mt-3">
              <Button type="submit" variant="contained" className="btn-primary m-2">
                <span className="btn-wrapper--label">Save</span>
                <span className="btn-wrapper--icon">
                  <FontAwesomeIcon icon={['far', 'save']} />
                </span>
              </Button>
              {id && (
                <>
                  <Button
                    type="button"
                    onClick={publishNow}
                    className="btn-primary m-2"
                    disabled={form.status === 'PUBLISHED'}
                  >
                    Publish Now
                  </Button>
                  <Button
                    type="button"
                    onClick={schedulePost}
                    className="btn-neutral-first m-2"
                  >
                    Schedule
                  </Button>
                </>
              )}
            </div>
          </Box>
        </Card>
        {loading && (
          <div className="text-center text-black-50 font-size-sm mt-2">
            Loading...
          </div>
        )}
      </WrapperSeamless>
    </Container>
  );
};

export default BlogForm;
